import { useCallback, useEffect, useMemo, useState } from 'react';
import { useJobStore } from '@core/job/useJobStore';
import {
  QuestionnaireQuestion,
  QuestionnaireResponseInput,
  ValueType,
} from '@core/generated/gql-types';

const usePostingQuestionnaire = (
  isRequired: boolean,
  tenantId?: string | null,
) => {
  const fetchJobPostingQuestionnaire = useJobStore(
    (state) => state.fetchJobPostingQuestionnaire,
  );
  const jobPostingQuestionnaire = useJobStore(
    (state) => state.jobPostingQuestionnaire,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [responses, setResponses] = useState<Record<string, string>>({});

  const fetchQuestionnaire = useCallback(async () => {
    if (!isRequired) {
      return;
    }

    setIsLoading(true);
    try {
      await fetchJobPostingQuestionnaire(tenantId || undefined);
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchJobPostingQuestionnaire, isRequired, tenantId]);

  useEffect(() => {
    if (isRequired) {
      fetchQuestionnaire();
    }
  }, [fetchQuestionnaire, isRequired]);

  const updateResponse = useCallback((questionId: string, value: string) => {
    setResponses((previousResponses) => ({
      ...previousResponses,
      [questionId]: value,
    }));
  }, []);

  const clearResponses = useCallback(() => {
    setResponses({});
  }, []);

  const formatValueForBackend = useCallback(
    (rawValue: string, valueType: ValueType): string => {
      if (!rawValue) {
        return valueType === ValueType.Array ? '[]' : '';
      }

      if (valueType === ValueType.Array) {
        const arrayValues = rawValue.split(',').filter(Boolean);
        return JSON.stringify(arrayValues);
      }

      return rawValue;
    },
    [],
  );

  const getQuestionnaireInput =
    useCallback((): QuestionnaireResponseInput[] => {
      if (!jobPostingQuestionnaire?.questions) {
        return [];
      }

      return jobPostingQuestionnaire.questions
        .filter(
          (question): question is QuestionnaireQuestion => question !== null,
        )
        .map((question) => {
          const rawValue = responses[question.id] || '';
          const valueType = question.answerType || ValueType.String;

          return {
            id: question.id,
            value: formatValueForBackend(rawValue, valueType),
            valueType,
          };
        });
    }, [formatValueForBackend, jobPostingQuestionnaire, responses]);

  const isQuestionnaireComplete = useCallback((): boolean => {
    if (!isRequired || !jobPostingQuestionnaire?.questions) {
      return true;
    }

    const requiredQuestions = jobPostingQuestionnaire.questions.filter(
      (question): question is QuestionnaireQuestion =>
        question !== null && question.required,
    );

    return requiredQuestions.every((question) => {
      const response = responses[question.id];
      return response !== undefined && response.trim() !== '';
    });
  }, [isRequired, jobPostingQuestionnaire, responses]);

  // Sort the questions by priority ascending and return the sorted questions
  const sortedQuestions = useMemo(() => {
    return [...(jobPostingQuestionnaire?.questions || [])].sort(
      (a, b) => (a?.priority || 0) - (b?.priority || 0),
    );
  }, [jobPostingQuestionnaire?.questions]);

  return {
    isRequired,
    isLoading,
    questions: sortedQuestions,
    responses,
    updateResponse,
    clearResponses,
    getQuestionnaireInput,
    isQuestionnaireComplete,
  };
};

export default usePostingQuestionnaire;
